import { forwardRef, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import { SnackbarContent, CustomContentProps, SnackbarKey, useSnackbar } from "notistack";
import { Badge, Button, Divider, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Trans } from "react-i18next";

import { useSignalRContext } from "../Contexts/SignalRContext";
import { NotificationType } from "../../types/Notifications/NotificationBase";
import { notificationTarget } from "../../utilities/helpers/notificationLinks";
import { useAccessibleTarget } from "../AccessLink/useAccessibleTarget";
import { Button as EclipseButton } from "../Buttons/Button";
import i18n from "../../i18n";

export default function GlobalNotificationListener() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { connection } = useSignalRContext();
  const [activeKeys, setActiveKeys] = useState<SnackbarKey[]>([]);

  useEffect(() => {
    if (!connection) return;

    const handleGlobalNotification = (
      rawMessage: string,
      notificationType: string,
      callerUsername: string,
      notificationTimestamp: string
    ) => {
      if (
        notificationType !== "StoppageSetAsAvailableNotification" &&
        notificationType !== "StoppageSetAsUnavailableNotification"
      ) return;

      const safeMessage = DOMPurify.sanitize(rawMessage);

      let notification: NotificationType | null = null;

      try {
        notification = JSON.parse(safeMessage) as NotificationType;
      } catch {
        // not JSON, leave as null
      }

      const key = enqueueSnackbar(safeMessage, {
        persist: true,
        anchorOrigin: { vertical: "top", horizontal: "left" },
        onExited: (_node, exitedKey) =>
          setActiveKeys(prev => prev.filter(k => k !== exitedKey)),
        content: (key, message) => (
          <CustomNotification
            id={key}
            message={notification ? renderNotification(notification, callerUsername) : message}
            notification={notification}
            notificationTimestamp={notificationTimestamp}
            persist={true}
            style={{}}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            variant={"default"}
            hideIconVariant={false}
            iconVariant={{}}
          />
        ),
      });

      setActiveKeys(prev => [...prev, key]);
    };

    connection.on("GlobalNotificationAsync", handleGlobalNotification);

    return () => {
      connection.off("GlobalNotificationAsync", handleGlobalNotification);
    };
  }, [connection, enqueueSnackbar, closeSnackbar]);

  const handleDismissAll = () => {
    // Hide the control immediately, then let the toasts animate out. onExited
    // filtering an already-empty list is a harmless no-op.
    const keys = activeKeys;
    setActiveKeys([]);
    keys.forEach(key => closeSnackbar(key));
  };

  const CustomNotification = forwardRef<
    HTMLDivElement,
    CustomContentProps & { notificationTimestamp?: string; notification?: NotificationType | null }
  >((props, ref) => {
    const { id, message, notificationTimestamp, notification, ...other } = props;
    const { closeSnackbar } = useSnackbar();

    return (
      <SnackbarContent ref={ref} {...other}>
        <Paper elevation={6} className="flex flex-col p-4 w-[360px] !bg-theme-surface-2 border border-theme-border">
          <Typography className="text-xl font-bold mb-2 italic text-theme-text" variant="h6">
            {i18n.t("notifications.title")}
          </Typography>

          <Divider className="my-2 bg-theme-border" />
          <Typography className="text-theme-text mt-2" variant="body1">{message}</Typography>
          <div className="flex justify-between items-center mt-4 gap-2">
            {
              notificationTimestamp && (
                <Typography className="text-theme-text-muted italic text-sm" variant="body2">
                  {notificationTimestamp}
                </Typography>
              )
            }
            <div className="flex items-center gap-2 ml-auto">
              {notification && (
                <StoppageLinkButton notification={notification} onNavigate={() => closeSnackbar(id)} />
              )}
              <Button

                size="small"
                className="!text-white !bg-theme-info hover:!bg-theme-info-hover"
                onClick={() => closeSnackbar(id)}
              >
                {i18n.t("actions.dismiss")}
              </Button>
            </div>
          </div>
        </Paper>
      </SnackbarContent>
    );
  });

  if (activeKeys.length <= 1) return null;

  // Sits beside the top-left notification stack so it never overlaps the cards.
  return (
    <div className="fixed top-3 left-[372px] z-[1401] animate-fade-in">
      <Tooltip title={i18n.t("notifications.dismissAll")} arrow placement="right">
        <Badge badgeContent={activeKeys.length} color="error" overlap="circular">
          <IconButton
            size="small"
            aria-label={i18n.t("notifications.dismissAll")}
            onClick={handleDismissAll}
            className="!text-white !bg-theme-info hover:!bg-theme-info-hover !shadow-md"
          >
            <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
          </IconButton>
        </Badge>
      </Tooltip>
    </div>
  );
}

/** Opens the stoppage the notification is about, for users who can reach the Stoppages page. */
export const StoppageLinkButton: React.FC<{ notification: NotificationType; onNavigate: () => void }> = ({
  notification,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const target = useAccessibleTarget(notificationTarget(notification));

  if (!target) return null;

  return (
    <EclipseButton
      variant="primary"
      size="sm"
      onClick={() => {
        onNavigate();
        navigate(target.to);
      }}
    >
      {i18n.t("notifications.viewStoppage")}
    </EclipseButton>
  );
};

const renderNotification = (notification: NotificationType, callerUsername?: string) => {
  switch (notification.type) {
    case "StoppageSetAsAvailableNotification":
      return (
        <Typography className="text-theme-text text-lg">
          <Trans
            i18nKey={callerUsername ? "notifications.setAvailableBy" : "notifications.setAvailable"}
            values={{ plant: notification.plantRef, user: callerUsername }}
            components={{
              plant: <span className="font-bold" style={{ color: notification.plantColour }} />,
              up: <span className="font-bold text-theme-success" />,
              user: <span className="font-bold" />,
            }}
          />
        </Typography>
      );

    case "StoppageSetAsUnavailableNotification":
      return (
        <Typography className="text-theme-text text-lg">
          <Trans
            i18nKey={callerUsername ? "notifications.setUnavailableBy" : "notifications.setUnavailable"}
            values={{ plant: notification.plantRef, status: notification.status, user: callerUsername }}
            components={{
              plant: <span className='font-bold' style={{ color: notification.plantColour }} />,
              status: <span className="font-bold" style={{ color: notification.statusColour }} />,
              user: <span className="font-bold" />,
            }}
          />
        </Typography>
      );

    default:
      return <Typography>{i18n.t("notifications.generic")}</Typography>;
  }
};